import React, { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import classnames from 'classnames';
import cond from 'lodash/fp/cond';
import flow from 'lodash/fp/flow';
import omit from 'lodash/fp/omit';
import { Button, Card } from 'react-bootstrap';
import { FieldPath, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import StepWizard from 'react-step-wizard';
import { toast } from 'react-toastify';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import WizardStepsNav from 'v2/common/WizardStepsNav/WizardStepsNav';
import { GenericObject, MatchedOrganizationEntity } from 'v2/models';
import { URL_REGEX } from 'v2/utils/formValidator';
import * as yup from 'yup';
import { addUserToWaitlist, fetchPermissionsAction } from '../../../actions';
import styles from './signUpFlow.module.scss';
import StepFour from './StepFour';
import StepOne from './StepOne';
import StepThree from './StepThree';
import StepTwo from './StepTwo';
import {
  CompanyDescriptions,
  CompanyDetails,
  isValidLinkedinUrl,
} from 'v2/utils/userUtils';
import useGetCompanyDetails from 'v2/hooks/useGetCompanyDetails';

interface DispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  addUserToWaitlist: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  fetchPermissionsAction: Function;
}

interface StateProps {
  email: string;
}

type SignUpFlowPros = DispatchProps & StateProps;

export interface SignUpFlowFormData {
  email: string;
  user_type: 'data_buyer' | 'data_seller' | '';
  first_name: string;
  last_name: string;
  job_title: string;
  firm_name: string;
  firm_type: string;
  linkedin_url: string;
  short_description: string;
  website: string;
  long_description: string;
  vague_description: string;
}

const STEP_NAMES = [
  'Welcome',
  'Account Information',
  'My Organization',
  'Final Details',
];

const schemaStepTwo = yup.object().shape({
  email: yup.string(),
  user_type: yup.string().required('Required'),
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  job_title: yup.string().required('Required'),
  linkedin_url: yup
    .string()
    .test('linkedin-url', 'Invalid LinkedIn URL', function (value) {
      if (!value) return true; // If value is empty, it's considered valid as it's optional
      return isValidLinkedinUrl(value);
    }),
});

const schemaStepThree = yup.object().shape({
  firm_name: yup.string().required('Required'),
  firm_type: yup.string().required('Required'),
  website: yup
    .string()
    .required('Required')
    .matches(URL_REGEX, { message: 'Please enter a valid URL' }),
  short_description: yup.string().required('Required'),
});

const schemaStepFour = yup.object().shape({
  long_description: yup.string().required('Required'),
  vague_description: yup.string().when('user_type', {
    is: 'data_buyer',
    then: yup.string().required('Required'),
    otherwise: yup.string().nullable(),
  }),
});

const schemeAllPages = schemaStepTwo
  .concat(schemaStepThree)
  .concat(schemaStepFour);

const schemasValidators = [
  null,
  schemaStepTwo,
  schemaStepThree,
  schemaStepFour,
];

const INITIAL_FORM_STATE: SignUpFlowFormData = {
  email: '',
  user_type: '',
  first_name: '',
  last_name: '',
  job_title: '',
  linkedin_url: '',
  firm_name: '',
  firm_type: '',
  website: '',
  short_description: '',
  long_description: '',
  vague_description: '',
};

const SignUpFlow = ({
  email,
  addUserToWaitlist,
  fetchPermissionsAction,
}: SignUpFlowPros) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    getValues,
    setValue,
    trigger,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: { ...INITIAL_FORM_STATE, email },
    mode: 'onChange',
    resolver: yupResolver(schemeAllPages),
  });
  const [isBusy, setIsBusy] = useState(false);
  const wizardInstance = useRef<GenericObject>();
  const [currentStep, setCurrentStep] = useState(1);
  const userType = watch('user_type');
  const formValues = getValues();
  const history = useHistory();

  const { matchedOrg, companyDetails, externalCompany } = useGetCompanyDetails({
    email,
    onProviderSuccess: (data) =>
      userType === 'data_seller' && fillOutSeller(data),
    onCompanySuccess: (data) => userType === 'data_buyer' && fillOutBuyer(data),
    onExternalCompanySuccess: (data) => data && fillOutBothUsers(data),
  });

  useEffect(() => {
    if (userType === 'data_seller') {
      fillOutSeller(matchedOrg);
    }
    if (userType === 'data_buyer') {
      fillOutBuyer(companyDetails);
    }
    externalCompany && fillOutBothUsers(externalCompany);
  }, [userType]);

  function fillOutSeller(matchedOrg?: MatchedOrganizationEntity) {
    if (matchedOrg) {
      !formValues.firm_name && setValue('firm_name', matchedOrg.name);
      !formValues.firm_type && setValue('firm_type', matchedOrg.classification);
      !formValues.short_description &&
        setValue('short_description', matchedOrg.short_description);
      !formValues.website && setValue('website', matchedOrg.url);
      !formValues.long_description &&
        setValue('long_description', matchedOrg.long_description);
      !formValues.vague_description &&
        setValue('vague_description', matchedOrg.vague_description);
    }
  }

  function fillOutBuyer(companyDetails?: CompanyDescriptions) {
    if (companyDetails) {
      !formValues.firm_type &&
        setValue('firm_type', companyDetails.classification);
      !formValues.short_description &&
        setValue('short_description', companyDetails.short_description);
      !formValues.long_description &&
        setValue('long_description', companyDetails.long_description);
      !formValues.vague_description &&
        setValue('vague_description', companyDetails.vague_description);
    }
  }

  function fillOutBothUsers(externalCompany: CompanyDetails) {
    !formValues.firm_name &&
      setValue('firm_name', externalCompany.display_name);
    !formValues.website && setValue('website', externalCompany.website);
  }

  const onSubmit = async () => {
    try {
      if (isFormValid()) {
        const values: SignUpFlowFormData = getValues();
        setIsBusy(true);
        const cleanedData = flow(
          omit('email'),
          cond([
            [
              (values: SignUpFlowFormData) => values.user_type === 'data_buyer',
              (values: SignUpFlowFormData) => values,
            ],
            [
              (values: SignUpFlowFormData) =>
                values.user_type === 'data_seller',
              (values: SignUpFlowFormData) => omit('vague_description')(values),
            ],
          ])
        )(values);
        await addUserToWaitlist(cleanedData);
        await fetchPermissionsAction();
        setIsBusy(false);
        history.push(
          values.user_type === 'data_buyer' ? '/authorize' : '/waitlist'
        );
      }
    } catch (error) {
      toast.error(error as AxiosError);
    }
  };

  const trimAllValues = () => {
    const currentValues = getValues();
    for (const key in currentValues) {
      let element = currentValues[key as keyof SignUpFlowFormData];
      element = element?.trim();
      if ((key as keyof SignUpFlowFormData) === 'long_description') {
        element = element?.replaceAll('&nbsp;', '');
      }
      if ((key as keyof SignUpFlowFormData) !== 'email') {
        setValue(key as keyof SignUpFlowFormData, element);
      }
    }
  };

  const isFormValid = () => {
    try {
      trimAllValues();
      if (Object.keys(errors).length) {
        return false;
      }
      schemasValidators[currentStep - 1]?.validateSync(getValues(), {
        abortEarly: false,
      });
      return true;
    } catch (error: unknown) {
      trigger(getFieldsWithError(error as yup.ValidationError));
      return false;
    }
  };

  function getFieldsWithError(error: yup.ValidationError) {
    const fieldsWithError = error?.inner
      .map((item) => item.path)
      .filter(Boolean);

    return fieldsWithError as
      | FieldPath<SignUpFlowFormData>
      | FieldPath<SignUpFlowFormData>[];
  }

  async function validateFields() {
    try {
      trimAllValues();
      await schemasValidators[currentStep - 1]?.validate(getValues(), {
        abortEarly: false,
      });
      wizardInstance.current?.nextStep();
      // TODO fix the click submit button
      setTimeout(() => {
        clearErrors();
      });
    } catch (error) {
      trigger(getFieldsWithError(error as yup.ValidationError));
    }
  }

  return (
    <div className="mt-4 container-md fix-bg-images">
      <BackgroundImages />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card
          className={classnames(
            'h-100 px-0 mt-5 shadow mx-auto',
            styles.cardRoot
          )}
        >
          <Card.Body>
            <StepWizard
              nav={<WizardStepsNav stepNames={STEP_NAMES} />}
              transitions={{}}
              isLazyMount={true}
              instance={(instance) => {
                wizardInstance.current = instance;
                wizardInstance.current.onStepChange = ({
                  activeStep,
                }: {
                  activeStep: number;
                }) => {
                  setCurrentStep(activeStep);
                };
              }}
            >
              <StepOne goNextStep={() => wizardInstance.current?.nextStep()} />
              <div
                className={classnames(
                  styles.signUpFlowStepContainer,
                  'mx-auto pt-3'
                )}
              >
                <StepTwo register={register} errors={errors} />
              </div>
              <div
                className={classnames(
                  styles.signUpFlowStepContainer,
                  'mx-auto pt-3'
                )}
              >
                <StepThree register={register} errors={errors} />
              </div>
              <div
                className={classnames(
                  styles.signUpFlowStepContainer,
                  'mx-auto pt-3'
                )}
              >
                <StepFour
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  formValues={getValues()}
                />
              </div>
            </StepWizard>
          </Card.Body>
          {currentStep > 1 ? (
            <Card.Footer className={'bg-white d-flex justify-content-between'}>
              {currentStep > 1 ? (
                <Button
                  onClick={() => wizardInstance.current?.previousStep()}
                  disabled={isBusy}
                >
                  Previous
                </Button>
              ) : null}

              {currentStep < STEP_NAMES.length ? (
                <Button
                  className="ml-auto"
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    validateFields();
                  }}
                  data-cy="signup:next-button"
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="ml-auto"
                  disabled={!isDirty || isBusy}
                  data-cy="signup:submit-button"
                >
                  {isBusy ? 'Loading...' : 'Submit'}
                </Button>
              )}
            </Card.Footer>
          ) : null}
        </Card>
      </form>
    </div>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    email: state.user.user.email,
  };
};

export default connect<StateProps, DispatchProps>(mapStateToProps, {
  addUserToWaitlist,
  fetchPermissionsAction,
})(SignUpFlow);
